import {
  AloftPriceJSON,
  CombinedManualAdjustmentType,
  FeatureAreaKey,
  UadQualityKey,
  featureAreaKeys,
  isDefined,
  schCombinedManualAdjustmentType,
  schFormNumber,
  schFormNumberStringOnly,
  uadQualityKeys,
} from '@aloft/toolkit-shared';

import { z } from 'zod';

// TODO: move general form types somewhere outside of the analysis module
import { schPrice } from '../../modules/analysis/types.ts';

const schAdjustmentException = z
  .array(
    z.object({
      adjustmentType: schCombinedManualAdjustmentType,
      value: schPrice,
    }),
  )
  .refine(
    (roundingAdjustments) => {
      const adjustmentTypes = roundingAdjustments.map((adjustment) => adjustment.adjustmentType);
      return new Set(adjustmentTypes).size === adjustmentTypes.length;
    },
    { message: 'Adjustment types must be unique' },
  )
  .transform((roundingAdjustments) => {
    const rounding: Partial<Record<CombinedManualAdjustmentType, AloftPriceJSON>> = {};
    roundingAdjustments.forEach((adjustment) => {
      if (isDefined(adjustment.value)) {
        rounding[adjustment.adjustmentType] = adjustment.value;
      }
    });
    return rounding;
  });

const emptyStringToUndefined = (arg: unknown) => {
  if (typeof arg === 'string' && arg === '') {
    return undefined;
  }
  return arg;
};

const schOptionalFormNumber = z.preprocess(emptyStringToUndefined, schFormNumber.optional());
const schOptionalFormNumberStringOnly = z.preprocess(
  emptyStringToUndefined,
  schFormNumberStringOnly.optional(),
);
const schRequiredFormNumber = z.preprocess(emptyStringToUndefined, schFormNumber);

const schTextBoolean = z
  .string()
  .regex(/[true|false]/)
  .transform((val) => val === 'true');

export const schSettingsForm = z.object({
  roundingDefault: schPrice.optional(),
  rounding: schAdjustmentException.optional(),
  thresholdDefault: schPrice.optional(),
  threshold: schAdjustmentException.optional(),
  depreciateSeparately: schTextBoolean.optional(),
  enableFunctionalDepreciation: schTextBoolean.optional(),
  enableExternalDepreciation: schTextBoolean.optional(),
  defaultEcoLife: schFormNumber.optional(),
  defaultEcoLifeManufactured: schFormNumber.optional(),
  defaultEcoLifeGarage: schFormNumber.optional(),
  defaultEcoLifeDetachedGarage: schFormNumber.optional(),
  defaultEcoLifeCarport: schFormNumber.optional(),
  defaultEcoLifePorch: schFormNumber.optional(),
  defaultEcoLifePatio: schFormNumber.optional(),
  defaultEcoLifeDeck: schFormNumber.optional(),
  defaultEcoLifeWorkshop: schFormNumber.optional(),
  defaultEcoLifeShed: schFormNumber.optional(),
  defaultEcoLifeBarn: schFormNumber.optional(),
  defaultEcoLifePool: schFormNumber.optional(),
  defaultEcoLifeSpa: schFormNumber.optional(),
  defaultEcoLifeADU: schFormNumber.optional(),
  defaultEcoLifeManualCost: schFormNumber.optional(),
  defaultEcoLifeFireplace: schFormNumber.optional(),
  costMultiplier: schOptionalFormNumberStringOnly,
  defaultFeatureAreas: z
    .object(
      Object.fromEntries(featureAreaKeys.map((k) => [k, schOptionalFormNumber])) as Record<
        FeatureAreaKey,
        typeof schOptionalFormNumber
      >,
    )
    .optional(),
  showFeatureAreasOnGrid: schTextBoolean.optional(),
  uadToCraftsmanQuality: z
    .object(
      Object.fromEntries(uadQualityKeys.map((k) => [k, schOptionalFormNumberStringOnly])) as Record<
        UadQualityKey,
        typeof schOptionalFormNumberStringOnly
      >,
    )
    .optional(),
  showCraftsmanQualityOnGrid: schTextBoolean.optional(),
});

export type SettingsFormOutput = z.output<typeof schSettingsForm>;

export const schCompGridSettingsForm = z.object({
  defaultFeatureAreas: z.object(
    Object.fromEntries(featureAreaKeys.map((k) => [k, schRequiredFormNumber])) as Record<
      FeatureAreaKey,
      typeof schRequiredFormNumber
    >,
  ),
  showFeatureAreasOnGrid: schTextBoolean.optional(),
  uadToCraftsmanQuality: z
    .object(
      Object.fromEntries(uadQualityKeys.map((k) => [k, schOptionalFormNumberStringOnly])) as Record<
        UadQualityKey,
        typeof schOptionalFormNumberStringOnly
      >,
    )
    .optional(),
  showCraftsmanQualityOnGrid: schTextBoolean.optional(),
});

export type CompGridSettingsFormOutput = z.output<typeof schCompGridSettingsForm>;
export type CompGridSettingsFormInput = z.input<typeof schCompGridSettingsForm>;
